import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DashboardKeyValue} from './models/dashboard-key-value';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {SpecialOrderService} from '../api/ms-specialorders/specialorder/specialorder.service';
import {ReferenceDataApi} from '../api/ms-partnumberchanges/reference-data.api';
import {UserService} from '../shared/services/user.service';
import {Permission} from '../shared/utils/permission.enum';
import {StatefulComponent} from '../shared/stateful/stateful.component';
import {RolesManagementStateModel} from '../settings/roles-management/users-table/roles-management-state.model';
import {finalize} from 'rxjs/operators';
import {SpinnerContextTypeEnum} from '../shared/spinner/spinner-context-type.enum';
import {SpinnerService} from '../shared/spinner/spinner.service';
import {ClaimsApi} from "../api/ms-claims/claims.api";

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent extends StatefulComponent<RolesManagementStateModel> implements OnInit {

    private subscriptions: Subscription = new Subscription();

    PNCs: DashboardKeyValue[] = [];
    SPOs: DashboardKeyValue[] = [];
    partnersTotal: DashboardKeyValue[] = [];
    partnersCurrent: DashboardKeyValue[] = [];

    claimsDashboardReady = false;
    spoDashboardReady = false;
    pncDashboardReady = false;

    hasPermissionsPNC = false;
    hasPermissionsSPO = false;
    hasPermissionsClaims = false;

    hasErrorPNC = false;
    hasErrorSPO = false;
    hasError = false;

    counterDisplayedTiles = 0;

    constructor(
        private router: Router,
        private specialOrderService: SpecialOrderService,
        private partNumberChangeService: ReferenceDataApi,
        private claimsService: ClaimsApi,
        private userService: UserService,
        private spinnerService: SpinnerService
    ) {
        super();
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    ngOnInit(): void {
        this.hasPermissionsPNC = this.userService.hasPermissions([
            Permission.ADMIN_PNC_VIEW,
            Permission.PARTNER_PNC_VIEW,
            Permission.PLANNER_PNC_VIEW,
            Permission.PRICE_PLANNER_PNC_VIEW,
            Permission.TECHNICIAN_PNC_VIEW,
            Permission.EE_SPECIALIST_PNC_VIEW
        ]);

        this.hasPermissionsSPO = this.userService.hasPermissions([
            Permission.ADMIN_SPO_VIEW,
            Permission.PARTNER_SPO_VIEW
        ]);

        this.hasPermissionsClaims = this.userService.hasPermissions([
            Permission.ADMIN_CLAIM_VIEW,
            Permission.PARTNER_CLAIM_VIEW
        ]);

        this.getDashboard();
    }

    getDashboard(): void {
        // PNC tile
        if (this.hasPermissionsPNC) {
            this.subscriptions.add(
                this.partNumberChangeService.getDashboard()
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                        this.pncDashboardReady = true;
                    }))
                    .subscribe((dashboardResult) => {

                        this.PNCs.push(new DashboardKeyValue('Total PNCs', '' + dashboardResult.totalPNCs));

                        if (dashboardResult.pending != null) {
                            this.PNCs.push(new DashboardKeyValue('Pending', '' + dashboardResult.pending));
                        }

                        if (dashboardResult.rejected != null) {
                            this.PNCs.push(new DashboardKeyValue('Rejected', '' + dashboardResult.rejected));
                        }

                        if (dashboardResult.analysis != null) {
                            this.PNCs.push(new DashboardKeyValue('Analysis', '' + dashboardResult.analysis));
                        }

                        if (dashboardResult.eeAnalysis != null) {
                            this.PNCs.push(new DashboardKeyValue('EE Analysis', '' + dashboardResult.eeAnalysis));
                        }

                        if (dashboardResult.solved != null) {
                            this.PNCs.push(new DashboardKeyValue('Solved', '' + dashboardResult.solved));
                        }

                    }, ()=> {
                        this.hasErrorPNC = true;
                    })
            )
        }

        // SPO tile
        if (this.hasPermissionsSPO) {
            this.subscriptions.add(
                this.specialOrderService.getDashboardSPOs()
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                        this.spoDashboardReady = true;
                    }))
                    .subscribe((dashboardResult) => {
                        this.SPOs.push(new DashboardKeyValue('Total SPOs', '' + dashboardResult.totalSPOs));
                        this.SPOs.push(new DashboardKeyValue('Without sales order number', '' + dashboardResult.withoutSalesOrderNumber));
                    }, ()=> {
                        this.hasErrorSPO = true;
                    })
            );
        }

        // Claims tiles information
        if (this.hasPermissionsClaims) {
            this.subscriptions.add(
                this.claimsService.getPartnerCodes()
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                        // Show first tile
                        this.getNext();
                        this.claimsDashboardReady = true;
                    }))
                    .subscribe((dashboardResult) => {
                        let unmarshalledMap = new Map<string, string>(Object.entries(dashboardResult));
                        unmarshalledMap.forEach((partnerName: string, partnerCode: string) => {
                            this.partnersTotal.push(new DashboardKeyValue(partnerCode, partnerName));
                        });
                    }, ()=> {
                        this.hasError = true;
                    })
            );
        } else {
            this.claimsDashboardReady = true;
        }
    }

    getNext(): void {
        if (this.partnersCurrent.length === this.partnersTotal.length) {
            return;
        }
        this.partnersCurrent.push(this.partnersTotal[this.counterDisplayedTiles++]);
    }

    navigate(path: string): void {
        this.router.navigate(['/', path]);
    }
}
